import React, { useEffect } from 'react';

import { format, isSameDay } from 'date-fns';
import { Calendar as CalendarIcon } from 'lucide-react';
import { DateRange } from 'react-day-picker';

import { Calendar } from './calendar';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import { cn } from '../../lib/utils';
import { Button } from './button';

export function DatePickerWithRange({
  className,
  onChange,
  preset,
  width = 300,
}: React.HTMLAttributes<HTMLDivElement> & { onChange: (_args: any) => any; preset: DateRange; width?: number }) {
  const [prev, setPrev] = React.useState<DateRange | undefined>(preset);
  const [date, setDate] = React.useState<DateRange | undefined>(preset);
  const [preventEmit, setPreventEmit] = React.useState<boolean>(false);

  const setConditionalDate = (d) => {
    if (!!d && isSameDay(prev.from, d.from)) {
      setDate({ from: d.to });
    } else {
      setDate({ ...d });
    }
  };

  const rangeChanged = (prev: DateRange, date: DateRange) =>
    !isSameDay(prev.from, date.from) || !isSameDay(prev.to, date.to);

  useEffect(() => {
    setPreventEmit(true);
    setDate(preset);
  }, [preset]);

  useEffect(() => {
    if (!!date && date.from && date.to && rangeChanged(prev, date)) {
      if (!preventEmit) {
        onChange(date);
      } else {
        setPreventEmit(false);
      }
      setPrev(date);
    } else {
      setPreventEmit(false);
    }
  }, [date]);

  return (
    <div className={cn('grid gap-2', className)}>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            id="date"
            variant={'outline'}
            className={cn(`w-[${width}] justify-start text-left font-normal`, !date && 'text-muted-foreground')}
          >
            <CalendarIcon />
            {date?.from ? (
              date.to ? (
                <>
                  {format(date.from, 'LLL dd, y')} - {format(date.to, 'LLL dd, y')}
                </>
              ) : (
                format(date.from, 'LLL dd, y')
              )
            ) : (
              <span>Pick a date</span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            initialFocus
            mode="range"
            defaultMonth={date?.from}
            selected={date}
            onSelect={setConditionalDate}
            numberOfMonths={2}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}
